import { useReducer } from 'react';

export interface ServiceRequestFormData {
  // legacy/workaround: BOS carries duplicate snake_case + camelCase keys for the
  // same property/suite id; both are kept until the submit payload is normalized.
  property_id?: string;
  propertyId?: string;
  suite_id?: string;
  suiteId?: string;

  serviceRequestType?: string;
  creatorId?: string;

  suiteFloorPLan?: string;
  annotatateImage?: boolean;

  asset_item_id?: string;
  asset_item_name?: string;
  asset_item_nameDescription?: string;
  asset_item_type?: string;
  asset_item_make?: string;
  asset_item_model?: string;
  asset_serial_number?: string;
  assetWarrantyExpiresAt?: string;
  assetId?: string;
  itemId?: string;
  viewAsset?: string;

  issueType?: string;
  issueTypeId?: string;

  category?: any;
  faq_id?: string;

  description?: string;
  srAttachments?: any[];
  uploadedImages?: any[];
  tags?: any[];
  schedulePreferences?: any;
  showImageLoading?: boolean;
}

export interface ServiceRequestFormFlowState {
  step: number;
  data: ServiceRequestFormData;
}

export interface UseServiceRequestFormFlowReturn {
  formData: ServiceRequestFormData;
  step: number;
  patchFormData: (partial: Partial<ServiceRequestFormData>) => void;
  goNext: () => void;
  goPrev: () => void;
  goToStep: (step: number) => void;
  reset: () => void;
  seed: (data: ServiceRequestFormData) => void;
}

type ServiceRequestFormFlowAction =
  | { type: 'PATCH'; payload: Partial<ServiceRequestFormData> }
  | { type: 'NEXT' }
  | { type: 'PREV' }
  | { type: 'GOTO'; step: number }
  | { type: 'RESET' }
  | { type: 'SEED'; payload: ServiceRequestFormData };

const buildInitialState = (
  initialData?: ServiceRequestFormData
): ServiceRequestFormFlowState => {
  return {
    step: 0,
    data: initialData ?? {},
  };
};

const formFlowReducer = (
  state: ServiceRequestFormFlowState,
  action: ServiceRequestFormFlowAction
): ServiceRequestFormFlowState => {
  switch (action.type) {
    case 'PATCH':
      return {
        ...state,
        data: { ...state.data, ...action.payload },
      };

    case 'NEXT':
      return { ...state, step: state.step + 1 };

    case 'PREV':
      return { ...state, step: state.step - 1 };

    case 'GOTO':
      return { ...state, step: action.step };

    case 'RESET':
      return { step: 0, data: {} };

    case 'SEED':
      return { step: 0, data: action.payload };

    default:
      return state;
  }
};

export const useServiceRequestFormFlow = (
  initialData?: ServiceRequestFormData
): UseServiceRequestFormFlowReturn => {
  const [state, dispatch] = useReducer(
    formFlowReducer,
    initialData,
    buildInitialState
  );

  const patchFormData = (partial: Partial<ServiceRequestFormData>) => {
    dispatch({ type: 'PATCH', payload: partial });
  };

  const goNext = () => {
    dispatch({ type: 'NEXT' });
  };

  const goPrev = () => {
    dispatch({ type: 'PREV' });
  };

  const goToStep = (step: number) => {
    dispatch({ type: 'GOTO', step });
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
  };

  const seed = (data: ServiceRequestFormData) => {
    dispatch({ type: 'SEED', payload: data });
  };

  return {
    formData: state.data,
    step: state.step,
    patchFormData,
    goNext,
    goPrev,
    goToStep,
    reset,
    seed,
  };
};